import React, { useState, useRef, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Fab from "@material-ui/core/Fab";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import bubbleIcon from "../../assets/logobolha.png";
import CloseIcon from "@material-ui/icons/Close";
import RemoveIcon from "@material-ui/icons/Remove";
import SendIcon from "@material-ui/icons/Send";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
	fab: {
		position: "fixed",
		zIndex: 1300,
		cursor: "grab",
		touchAction: "none",
		"@media print": {
			display: "none !important",
		},
	},
	panel: {
		position: "fixed",
		width: 340,
		maxWidth: "90vw",
		height: 440,
		maxHeight: "80vh",
		display: "flex",
		flexDirection: "column",
		zIndex: 1300,
		overflow: "hidden",
		"@media print": {
			display: "none !important",
		},
	},
	header: {
		padding: theme.spacing(1, 1, 1, 2),
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
		cursor: "grab",
		touchAction: "none",
		userSelect: "none",
	},
	headerActions: {
		display: "flex",
		alignItems: "center",
	},
	messages: {
		flex: 1,
		overflowY: "auto",
		padding: theme.spacing(1.5),
		...theme.scrollbarStyles,
	},
	bubbleRow: {
		display: "flex",
		marginBottom: theme.spacing(1.5),
	},
	bubbleRowUser: {
		justifyContent: "flex-end",
	},
	bubble: {
		maxWidth: "85%",
		padding: theme.spacing(1, 1.5),
		borderRadius: 12,
		fontSize: 13,
		whiteSpace: "pre-wrap",
	},
	bubbleUser: {
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
		borderBottomRightRadius: 2,
	},
	bubbleAssistant: {
		backgroundColor: theme.mode === "light" ? "#f0f0f0" : "#333",
		borderBottomLeftRadius: 2,
	},
	categoryTag: {
		display: "block",
		fontSize: 11,
		opacity: 0.7,
		marginTop: 4,
	},
	inputRow: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(1),
		borderTop: `1px solid ${theme.mode === "light" ? "#e0e0e0" : "#444"}`,
	},
}));

const INITIAL_HISTORY = [
	{
		role: "assistant",
		text: "Oi! Eu sou a Voice. Pode perguntar qualquer coisa sobre como usar o sistema, consultar seus dados, concluir tarefas ou agendar algo.",
	},
];

const STORAGE_KEY = "tutorialAssistantPosition";
// Posicao padrao um pouco mais alta que o canto (bottom maior) pra nao
// nascer em cima da barra de icones do composer da conversa - mesmo
// assim da pra arrastar pra qualquer lugar, a posicao fica salva.
const DEFAULT_POSITION = { right: 24, bottom: 96 };
const FAB_SIZE = 56;
const DRAG_THRESHOLD = 4;
const PANEL_WIDTH = 340;
const PANEL_HEIGHT = 440;

const loadPosition = () => {
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		if (saved) return JSON.parse(saved);
	} catch (e) {
		// ignora e usa o padrao
	}
	return DEFAULT_POSITION;
};

const clampPosition = (pos) => {
	const maxRight = Math.max(8, window.innerWidth - FAB_SIZE - 8);
	const maxBottom = Math.max(8, window.innerHeight - FAB_SIZE - 8);
	return {
		right: Math.min(Math.max(pos.right, 8), maxRight),
		bottom: Math.min(Math.max(pos.bottom, 8), maxBottom),
	};
};

// A bolha fechada so precisa caber ela mesma (56px), mas o painel aberto
// e' bem maior (340x440) - se a bolha foi arrastada perto do topo/borda,
// usar a mesma posicao pro painel derrubava o cabecalho (com os botoes
// de minimizar/fechar) pra fora da tela, sem jeito de fechar sem F5.
// Recalcula uma posicao so pro painel, sem alterar/perder a posicao
// salva da bolha.
const clampPanelPosition = (pos) => {
	const panelWidth = Math.min(PANEL_WIDTH, window.innerWidth * 0.9);
	const panelHeight = Math.min(PANEL_HEIGHT, window.innerHeight * 0.8);
	const maxRight = Math.max(8, window.innerWidth - panelWidth - 8);
	const maxBottom = Math.max(8, window.innerHeight - panelHeight - 8);
	return {
		right: Math.min(Math.max(pos.right, 8), maxRight),
		bottom: Math.min(Math.max(pos.bottom, 8), maxBottom),
	};
};

const TutorialAssistantBubble = () => {
	const classes = useStyles();
	const [open, setOpen] = useState(false);
	const [minimized, setMinimized] = useState(false);
	const [question, setQuestion] = useState("");
	const [sending, setSending] = useState(false);
	const [history, setHistory] = useState(INITIAL_HISTORY);
	const [position, setPosition] = useState(loadPosition);
	const messagesEndRef = useRef(null);
	const dragState = useRef(null);

	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ block: "end" });
		}
	}, [history, sending, open]);

	const persistPosition = useCallback((pos) => {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
		} catch (e) {
			// localStorage indisponivel, so nao persiste - segue o baile
		}
	}, []);

	// Zoom do navegador muda window.innerWidth/innerHeight (em pixels CSS)
	// sem necessariamente disparar outro re-render do componente - sem
	// isso, a bolha/painel so' recalculava a posicao clampada na proxima
	// vez que algo mais mudasse o estado. Forca o recalculo (e persiste a
	// correcao) assim que o viewport mudar de tamanho, inclusive por zoom.
	useEffect(() => {
		const handleResize = () => {
			setPosition((prev) => {
				const clamped = clampPosition(prev);
				if (clamped.right !== prev.right || clamped.bottom !== prev.bottom) {
					persistPosition(clamped);
					return clamped;
				}
				return prev;
			});
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [persistPosition]);

	const handlePointerDown = (e) => {
		dragState.current = {
			startX: e.clientX,
			startY: e.clientY,
			startRight: position.right,
			startBottom: position.bottom,
			dragged: false,
		};
		e.currentTarget.setPointerCapture(e.pointerId);
	};

	const handlePointerMove = (e) => {
		if (!dragState.current) return;
		const dx = e.clientX - dragState.current.startX;
		const dy = e.clientY - dragState.current.startY;
		if (!dragState.current.dragged && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
		dragState.current.dragged = true;
		const next = clampPosition({
			right: dragState.current.startRight - dx,
			bottom: dragState.current.startBottom - dy,
		});
		setPosition(next);
	};

	const handlePointerUp = (e, onClickIfNotDragged) => {
		const wasDragged = dragState.current && dragState.current.dragged;
		if (wasDragged) {
			persistPosition(position);
		}
		dragState.current = null;
		if (!wasDragged && onClickIfNotDragged) {
			onClickIfNotDragged();
		}
	};

	const handleSend = async () => {
		if (!question.trim() || sending) return;
		const asked = question.trim();
		// Manda as ultimas trocas junto - sem isso, uma resposta curta a uma
		// pergunta de esclarecimento da IA (ex: "pelo Luca") vira uma
		// pergunta isolada sem contexto nenhum do lado do backend.
		const historyForRequest = history.slice(-6).map((h) => ({ role: h.role, text: h.text }));
		setHistory((prev) => [...prev, { role: "user", text: asked }]);
		setQuestion("");
		setSending(true);
		try {
			const { data } = await api.post("/tutorial-assistant/ask", { question: asked, history: historyForRequest });
			setHistory((prev) => [
				...prev,
				{
					role: "assistant",
					text: data.answer,
					category: data.categoryName,
				},
			]);
		} catch (err) {
			toastError(err);
			setHistory((prev) => [
				...prev,
				{
					role: "assistant",
					text: "Deu um erro ao buscar a resposta. Tenta de novo em instantes.",
				},
			]);
		} finally {
			setSending(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	if (!open || minimized) {
		// Corrigido 2026-10-21: a posicao salva e' em pixels fixos desde a
		// borda da tela - se a bolha foi arrastada e depois o usuario muda
		// o zoom do navegador (o que encolhe/cresce window.innerWidth em
		// pixels CSS), o "right"/"bottom" salvo pode passar a exceder o
		// viewport novo e empurrar a bolha pra fora da tela, invisivel e
		// sem como arrastar de volta (so dava pra resolver limpando o
		// localStorage). O painel aberto ja' era clampado assim (ver
		// panelPosition abaixo) - faltava aplicar o mesmo na bolha fechada.
		const fabPosition = clampPosition(position);
		return (
			<Fab
				color="primary"
				className={classes.fab}
				style={{ right: fabPosition.right, bottom: fabPosition.bottom }}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={(e) => handlePointerUp(e, () => {
					setOpen(true);
					setMinimized(false);
				})}
			>
				<img
					src={bubbleIcon}
					alt=""
					draggable={false}
					style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
				/>
			</Fab>
		);
	}

	const panelPosition = clampPanelPosition(position);

	return (
		<Paper
			elevation={6}
			className={classes.panel}
			style={{ right: panelPosition.right, bottom: panelPosition.bottom }}
		>
			<div
				className={classes.header}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
					<DragIndicatorIcon fontSize="small" style={{ opacity: 0.7 }} />
					<Typography variant="subtitle2">Voice</Typography>
				</div>
				<div className={classes.headerActions}>
					<IconButton
						size="small"
						style={{ color: "#fff" }}
						onPointerDown={(e) => e.stopPropagation()}
						onClick={() => setMinimized(true)}
					>
						<RemoveIcon fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						style={{ color: "#fff" }}
						onPointerDown={(e) => e.stopPropagation()}
						onClick={() => setOpen(false)}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</div>
			</div>
			<div className={classes.messages}>
				{history.map((msg, index) => (
					<div
						key={index}
						className={`${classes.bubbleRow} ${msg.role === "user" ? classes.bubbleRowUser : ""}`}
					>
						<div
							className={`${classes.bubble} ${
								msg.role === "user" ? classes.bubbleUser : classes.bubbleAssistant
							}`}
						>
							{msg.text}
							{msg.category && (
								<span className={classes.categoryTag}>📚 {msg.category}</span>
							)}
						</div>
					</div>
				))}
				{sending && (
					<div className={classes.bubbleRow}>
						<div className={`${classes.bubble} ${classes.bubbleAssistant}`}>
							<CircularProgress size={14} style={{ marginRight: 6 }} />
							digitando...
						</div>
					</div>
				)}
				<div ref={messagesEndRef} />
			</div>
			<div className={classes.inputRow}>
				<TextField
					fullWidth
					size="small"
					placeholder="Pergunte algo sobre o sistema..."
					value={question}
					onChange={(e) => setQuestion(e.target.value)}
					onKeyDown={handleKeyDown}
					multiline
					maxRows={3}
				/>
				<IconButton size="small" color="primary" onClick={handleSend} disabled={sending}>
					<SendIcon fontSize="small" />
				</IconButton>
			</div>
		</Paper>
	);
};

export default TutorialAssistantBubble;
